import { createRouter, createWebHistory } from "vue-router";
const Welcome = () => import("@/views/Welcome.vue");
const PanelAdmin = () => import("@/views/PanelAdmin.vue");
const VirtualReality = () => import("@/views/VirtualReality.vue");
const ProfileTemplate = () => import("../views/ProfileTemplate.vue");
const Rtl = () => import("@/views/Rtl.vue");
const SignIn = () => import("@/views/SignIn.vue");
const SignUp = () => import("@/views/SignUp.vue");
const Login = () => import("@/views/Login.vue");
const Register = () => import("@/views/Register.vue");
const SendEmail = () => import("../views/SendEmail.vue");
const ResetPassword = () => import("../views/ResetPassword.vue");
const Profile = () => import("@/views/Profile.vue");
const Users = () => import("../views/Users.vue");
import store from "/src/store";
import guest from "../middlewares/guest.js";
import auth from "../middlewares/auth.js";
import axios from "axios";

function middlewarePipeline(context, middleware, index) {
  const nextMiddleware = middleware[index];

  if (!nextMiddleware) {
    return context.next;
  }

  return () => {
    const nextPipeline = middlewarePipeline(context, middleware, index + 1);

    nextMiddleware({ ...context, next: nextPipeline });
  };
}

// Middleware para verificar permisos de módulo
const checkModulePermission = (requiredModule) => {
  return ({ next, store }) => {
    // Obtener permisos del usuario desde el store (auth module o fallback)
    const userPermissions = store.state.auth?.user?.permissions || store.state.user?.permissions || {};
    
    // Verificar si el módulo requiere un permiso específico
    if (!requiredModule) {
      return next();
    }
    
    // Verificar si el usuario tiene permiso para el módulo requerido (backend devuelve 1 o true)
    const hasPermission = !!userPermissions[requiredModule];
    
    if (hasPermission) {
      return next();
    } else {
      console.warn(`Acceso denegado al módulo: ${requiredModule}. Permisos disponibles:`, userPermissions);
      // Redirigir a página de acceso denegado
      return next({ name: 'AccessDenied' });
    }
  };
};

const routes = [
  {
    path: "/",
    name: "/",
    redirect: "/welcome",
  },
  {
    path: "/welcome",
    name: "Welcome",
    component: Welcome,
    meta: {
      requiresAuth: true,
      middleware: [auth],
      isWelcome: true
    }
  },
  {
    path: "/panel-admin",
    name: "PanelAdmin",
    component: PanelAdmin,
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('panel_admin')],
      requiredModule: 'panel_admin'
    }
  },
  {
    path: "/tables",
    redirect: "/panel-admin"
  },
  {
    path: "/carga-masiva",
    redirect: "/cargas-masivas-v2"
  },
  {
    path: "/virtual-reality",
    name: "Virtual Reality",
    component: VirtualReality,
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('panel_admin')],
      requiredModule: 'panel_admin'
    }
  },
  {
    path: "/profileTemplate",
    name: "ProfileTemplate",
    component: ProfileTemplate,
    meta: {
      requiresAuth: true,
      middleware: [auth]
    }
  },
  {
    path: "/rtl-page",
    name: "Rtl",
    component: Rtl,
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('panel_admin')],
      requiredModule: 'panel_admin'
    }
  },
  {
    path: "/actualizacion-masiva",
    name: "ActualizacionMasiva",
    component: () => import("@/views/ActualizacionMasiva.vue"),
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('bulk_update')],
      requiredModule: 'bulk_update'
    }
  },
  {
    path: "/actualizacion-masiva-v2",
    name: "ActualizacionMasivav2",
    component: () => import("@/views/ActualizacionMasivav2.vue"),
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('bulk_update_v2')],
      requiredModule: 'bulk_update_v2'
    }
  },
  {
    path: "/sign-in",
    name: "Sign In",
    component: SignIn,
    meta: {
      middleware: [guest]
    }
  },
  {
    path: "/sign-up",
    name: "Sign Up",
    component: SignUp,
    meta: {
      middleware: [guest]
    }
  },
  {
    path: "/login",
    name: "Login",
    component: Login,
    meta: {
      middleware: [guest]
    }
  },
  {
    path: "/register",
    name: "Register",
    component: Register,
    meta: {
      middleware: [guest]
    }
  },
  {
    path: "/password/email",
    name: "SendEmail",
    component: SendEmail,
    meta: {
      middleware: [guest]
    }
  },
  {
    path: "/password/reset",
    name: "ResetPassword",
    component: ResetPassword,
    meta: {
      middleware: [guest]
    }
  },
  {
    path: "/profile",
    name: "Profile",
    component: Profile,
    meta: {
      requiresAuth: true,
      middleware: [auth]
    }
  },
  // Nueva ruta para Cargar Export (antes de Activity)
  {
    path: "/cargar-export",
    name: "CargarExport",
    component: () => import("@/views/CargarExport.vue"),
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('cargar_export')],
      requiredModule: 'cargar_export'
    }
  },
  // Nueva ruta para Historial de Cargas
  {
    path: "/historial-cargas",
    name: "HistorialCargas",
    component: () => import("@/views/HistorialCargas.vue"),
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('cargar_export')],
      requiredModule: 'cargar_export'
    }
  },
  // Nueva ruta para Actividad (antes de Users)
  {
    path: "/activity",
    name: "Activity",
    component: () => import("@/views/Activity.vue"),
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('activity')],
      requiredModule: 'activity'
    }
  },
  {
    path: "/permisos-4tool",
    name: "Permisos4Tool",
    component: () => import("@/views/Permisos4Tool.vue"),
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('permisos_4tool')],
      requiredModule: 'permisos_4tool'
    }
  },
  {
    path: "/users",
    name: "Users",
    component: Users,
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('users')],
      requiredModule: 'users'
    }
  },
  // Nueva ruta para Master 4Tool
  {
    path: "/master-4tool",
    name: "Master4Tool",
    component: () => import("@/views/Master4Tool.vue"),
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('master_4tool')],
      requiredModule: 'master_4tool'
    }
  },
  // Nueva ruta para RegistroBorrado
  {
    path: "/RegistroBorrado",
    name: "BorradoRegistros",
    component: () => import("@/views/BorradoRegistros.vue"),
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('delete_records')],
      requiredModule: 'delete_records'
    }
  },
  {
    path: "/4task",
    name: "4Task",
    component: () => import("@/views/TaskDashboard.vue"),
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('4task')],
      requiredModule: '4task',
      hideNavigation: true,
      preloader: true,
      loadingMessage: "Cargando workspace de tareas...",
      moduleInfo: {
        title: "4Task",
        icon: "ri-task-line",
        color: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)"
      }
    }
  },
  {
    path: "/timesheet",
    name: "Timesheet",
    component: () => import("@/views/Timesheet.vue"),
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('4task')],
      requiredModule: '4task',
      hideNavigation: true
    }
  },
  // Página de acceso denegado
  {
    path: "/access-denied",
    name: "AccessDenied",
    component: () => import("@/views/AccessDenied.vue"),
    meta: {
      requiresAuth: true,
      middleware: [auth]
    }
  },  
  // Cargas Masivas
  {
    path: "/cargas-masivas-v2",
    name: "CargasMasivasV2",
    component: () => import("@/views/CargasMasivasV2/CargasMasivasV2.vue"),
    meta: {
      requiresAuth: true,
      middleware: [auth, checkModulePermission('bulk_loads')],
      requiredModule: 'bulk_loads'
    }
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  linkActiveClass: "active",
});

// Middleware global para verificar permisos cuando cambia la ruta
router.beforeEach(async (to, from, next) => {
  const context = {
    to,
    from,
    next,
    store,
  };

  // Si no tiene configuración de middleware, aplicar verificación de autenticación
  if (!to.meta.middleware) {
    return auth({ ...context, next: next });
  }

  // Verificar si necesitamos cargar los permisos del usuario
  if (to.meta.requiresAuth && 
      (!store.state.user || !store.state.user.permissions || Object.keys(store.state.user.permissions).length === 0)) {
    try {
      // Intentar obtener permisos del usuario si el store no los tiene
      const token = localStorage.getItem('auth_token');
      if (token) {
        const apiUrl = process.env.VUE_APP_API_BASE_URL || 'http://teleconnect.test/api/v2';
        
        // Obtener datos del usuario y permisos en paralelo
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        };

        const [userResponse, permissionsResponse] = await Promise.all([
          axios.get(`${apiUrl}/me`, { headers }),
          axios.get(`${apiUrl}/me/permissions`, { headers })
        ]);

        if (userResponse.data && userResponse.data.data) {
          store.commit('setUser', userResponse.data.data);
        }

        if (permissionsResponse.data && permissionsResponse.data.success && permissionsResponse.data.data) {
          store.commit('setUserPermissions', permissionsResponse.data.data);
        } else {
          console.warn("No se pudieron cargar los permisos: formato inválido");
        }
      } else {
        console.warn("No hay token de autenticación disponible");
      }
    } catch (error) {
      console.error('Error al obtener información del usuario:', error);
    }
  }

  // Verificar si el usuario intenta acceder a una ruta para la cual no tiene permiso
  if (to.meta.requiredModule) {
    const userPermissions = store.state.user?.permissions || {};
    const requiredModule = to.meta.requiredModule;
    
    // Si no tiene el permiso específico y no es la página de acceso denegado o bienvenida, redirigir
    if (!userPermissions[requiredModule] && to.name !== 'AccessDenied' && to.name !== 'Welcome') {
      console.warn(`Redirigiendo: usuario sin permiso para módulo ${requiredModule}`);
      
      // Si el usuario no tiene permisos para ningún módulo, ir a welcome
      const hasAnyPermission = Object.values(userPermissions).some(permission => permission === true);
      if (!hasAnyPermission) {
        return next({ name: 'Welcome' });
      }
      
      return next({ name: 'AccessDenied' });
    }
  }

  // Aplicar pipeline de middleware
  const middleware = to.meta.middleware;
  return middleware[0]({
    ...context,
    next: middlewarePipeline(context, middleware, 1),
  });
});

export default router;